import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, ImagePlus, Loader2, MessageCircle, Send, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PKG_NAMES = { essentials: "The Essentials", full_pour: "The Full Pour", zero_proof: "The Zero Proof" };
const digits = (p) => (p || "").replace(/[^+\d]/g, "");
const smsLink = (enq) =>
  `sms:${digits(enq.phone)}?&body=${encodeURIComponent(
    `Hi ${enq.name?.split(" ")[0] || "there"}! It's Shaken Society — about your ${enq.event_date || "event"}. `
  )}`;

export default function OwnerBookingCard({ enq, onChanged }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [replies, setReplies] = useState(enq.replies || []);
  const fileRef = useRef(null);

  const loadPhotos = async () => {
    try {
      const { data } = await axios.get(`${API}/owner/photos?reference=${enq.reference}`, { withCredentials: true });
      setPhotos(data.photos);
    } catch {
      /* photos are optional */
    }
  };

  useEffect(() => {
    loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await axios.post(`${API}/owner/enquiries/${enq.reference}/photos`, fd, { withCredentials: true });
        ok += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Could not upload ${f.name}`);
      }
    }
    setUploading(false);
    if (ok) {
      toast.success(`${ok} photo${ok > 1 ? "s" : ""} added`);
      loadPhotos();
      onChanged();
    }
  };

  const delPhoto = async (id) => {
    try {
      await axios.delete(`${API}/owner/photos/${id}`, { withCredentials: true });
      setPhotos((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not remove that photo.");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/owner/enquiries/${enq.reference}/reply`, { message: reply.trim() }, { withCredentials: true });
      toast.success("Reply sent!");
      setReplies((r) => [...r, { at: new Date().toISOString(), message: reply.trim() }]);
      setReply("");
      setReplyOpen(false);
      onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send that reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid={`owner-booking-card-${enq.reference}`}
      className="rounded-2xl border border-cream/15 bg-white/[0.03] p-5 transition-colors hover:border-cream/30"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-cream/10 px-3 py-1 text-[11px] tracking-[0.14em] text-gold">{enq.reference}</span>
        <span
          className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${
            enq.intent === "booking" ? "bg-gold text-espresso" : "bg-cream/10 text-cream/70"
          }`}
        >
          {enq.intent === "booking" ? "Instant booking" : "Quote request"}
        </span>
        {enq.deposit_paid && (
          <span
            className="rounded-full bg-gold/20 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-gold"
            data-testid={`owner-deposit-paid-${enq.reference}`}
          >
            Deposit paid
          </span>
        )}
      </div>

      <p className="mt-3 font-display text-xl text-cream">{enq.name}</p>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-cream/65">
        {enq.event_date && <span>{enq.event_date}</span>}
        {enq.event_type && <span>{enq.event_type}</span>}
        {enq.guests ? <span>{enq.guests} guests</span> : null}
        {enq.package && <span className="text-gold">{PKG_NAMES[enq.package] || enq.package}</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-cream/50">
        {enq.phone && (
          <a href={`tel:${digits(enq.phone)}`} className="flex items-center gap-1.5 transition-colors hover:text-gold">
            <Phone className="h-3.5 w-3.5" /> {enq.phone}
          </a>
        )}
        {enq.email && (
          <a href={`mailto:${enq.email}`} className="flex items-center gap-1.5 transition-colors hover:text-gold">
            <Mail className="h-3.5 w-3.5" /> {enq.email}
          </a>
        )}
      </div>
      {enq.message && <p className="mt-3 text-sm leading-relaxed text-cream/60">“{enq.message}”</p>}

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`owner-photos-${enq.reference}`}>
        {photos.map((p) => (
          <span key={p.id} className="group relative">
            <a href={`${API}/owner/photos/${p.id}`} target="_blank" rel="noopener noreferrer">
              <img
                src={`${API}/owner/photos/${p.id}`}
                alt={p.filename}
                className="h-20 w-20 rounded-xl border border-cream/15 object-cover transition-transform duration-300 hover:scale-105"
              />
            </a>
            <button
              data-testid={`owner-photo-delete-${p.id}`}
              onClick={() => delPhoto(p.id)}
              aria-label="Remove photo"
              className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-espresso text-cream/70 opacity-0 transition-opacity hover:text-rose group-hover:opacity-100"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid={`owner-photo-input-${enq.reference}`} />
        <button
          data-testid={`owner-photo-upload-${enq.reference}`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-cream/25 text-cream/50 transition-colors hover:border-gold hover:text-gold disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          <span className="text-[9px] uppercase tracking-[0.12em]">{uploading ? "Adding…" : "Photos"}</span>
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {enq.phone && (
          <a
            href={`tel:${digits(enq.phone)}`}
            data-testid={`owner-call-${enq.reference}`}
            className="flex items-center gap-1.5 rounded-full border border-cream/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-cream transition-colors hover:border-gold hover:text-gold"
          >
            <Phone className="h-3.5 w-3.5" /> Call
          </a>
        )}
        {enq.phone && (
          <a
            href={smsLink(enq)}
            data-testid={`owner-text-${enq.reference}`}
            className="flex items-center gap-1.5 rounded-full border border-cream/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-cream transition-colors hover:border-gold hover:text-gold"
          >
            <MessageCircle className="h-3.5 w-3.5" /> Text
          </a>
        )}
        {enq.email ? (
          <button
            data-testid={`owner-reply-toggle-${enq.reference}`}
            onClick={() => setReplyOpen((o) => !o)}
            className="flex items-center gap-1.5 rounded-full border border-gold/50 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-gold transition-colors hover:bg-gold hover:text-espresso"
          >
            <Mail className="h-3.5 w-3.5" /> {replyOpen ? "Cancel reply" : "Reply by email"}
          </button>
        ) : (
          <span className="flex items-center gap-1.5 text-[11px] tracking-wide text-cream/40">
            No email on file — call or text instead
          </span>
        )}
      </div>

      {replyOpen && (
        <div className="mt-3 rounded-xl border border-cream/15 bg-white/[0.04] p-3" data-testid={`owner-reply-box-${enq.reference}`}>
          <textarea
            data-testid={`owner-reply-input-${enq.reference}`}
            rows={4}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={`Hi ${enq.name?.split(" ")[0] || "there"} — thanks for reaching out to Shaken Society! …`}
            className="w-full resize-none rounded-lg border border-cream/20 bg-white/[0.04] p-3 text-sm text-cream outline-none transition-colors focus:border-gold"
          />
          <button
            data-testid={`owner-reply-send-${enq.reference}`}
            onClick={sendReply}
            disabled={sending || !reply.trim()}
            className="mt-2 flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-espresso transition-all hover:bg-cream disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send reply
          </button>
        </div>
      )}

      {replies.length > 0 && (
        <div className="mt-3 space-y-1" data-testid={`owner-replies-${enq.reference}`}>
          {replies.map((r, i) => (
            <p key={i} className="text-[11px] text-cream/40">
              Replied {r.at ? String(r.at).slice(0, 10) : ""}: “{r.message?.slice(0, 80)}{r.message?.length > 80 ? "…" : ""}”
            </p>
          ))}
        </div>
      )}
    </div>
  );
}